import { Alert, Avatar, Box, Button, Chip, Grid, Skeleton, Stack, Typography } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { Link as RouterLink, useParams } from "react-router";
import { useCompanyProfile } from "../hooks/useApiQueries.js";
import { ContentCard } from "../components/ContentCard.js";
import { SEO } from "../components/SEO.js";
import { NeonEyebrow, NeonDivider, neonButtonSx } from "../components/Neon.js";

// Strip the scheme/path noise off a website URL for display ("https://acme.com/x"
// → "acme.com"). Falls back to the raw string if it isn't parseable.
function prettyDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export function CompanyProfile() {
  const { slug = "" } = useParams();
  const { data, isLoading, error } = useCompanyProfile(slug);

  if (isLoading) return <Skeleton variant="rectangular" height={300} />;
  if (error || !data) return <Alert severity="error">Company not found.</Alert>;

  const { company, content } = data;

  return (
    <>
      <SEO
        title={`${company.name} — on Corian Park`}
        description={
          company.description ??
          `${company.name} — equipment, videos, and articles featured on Corian Park.`
        }
        canonical={`https://corianpark.au/company/${company.slug}`}
        ogImage={company.logoUrl ?? undefined}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: company.name,
            url: company.website ?? `https://corianpark.au/company/${company.slug}`,
            ...(company.logoUrl ? { logo: company.logoUrl } : {}),
            ...(company.description ? { description: company.description } : {}),
          },
        ]}
      />

      {/* Header band */}
      <Box
        sx={{
          mx: { xs: -2, sm: -3 },
          mt: -4,
          mb: { xs: 4, md: 6 },
          py: { xs: 5, md: 7 },
          px: { xs: 2, sm: 3, md: 4 },
          background:
            "linear-gradient(135deg, rgba(255,176,0,0.10) 0%, rgba(255,136,0,0.02) 60%, transparent 100%)",
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            bottom: 0,
            width: "100%",
            height: 3,
            background: "linear-gradient(90deg, #ffb000 0%, #ff8800 50%, transparent 100%)",
            boxShadow: "0 0 12px rgba(255,176,0,0.7)",
          }}
        />
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <NeonEyebrow glow>COMPANY</NeonEyebrow>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            sx={{ alignItems: { sm: "center" }, mt: 2 }}
          >
            <Avatar
              src={company.logoUrl ?? undefined}
              variant="rounded"
              sx={{
                width: 96,
                height: 96,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                color: "primary.main",
                fontWeight: 900,
                fontSize: 44,
                flexShrink: 0,
                boxShadow: "0 0 30px rgba(255,176,0,0.35)",
              }}
            >
              {company.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: 32, md: 52 },
                  fontWeight: 900,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.05,
                }}
              >
                {company.name}
              </Typography>
              {company.website && (
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}>
                  <Chip
                    component="a"
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    clickable
                    icon={<LanguageIcon sx={{ fontSize: 16 }} />}
                    label={prettyDomain(company.website)}
                    sx={{
                      bgcolor: "background.paper",
                      "&:hover": { borderColor: "primary.main", color: "primary.main" },
                    }}
                  />
                </Stack>
              )}
            </Box>
            <Button
              component={RouterLink}
              to="/quote"
              variant="contained"
              color="primary"
              size="large"
              startIcon={<RequestQuoteIcon />}
              sx={{ ...neonButtonSx, flexShrink: 0 }}
            >
              Get a quote
            </Button>
          </Stack>
        </Box>
      </Box>

      {company.description && (
        <Box sx={{ mb: 4, maxWidth: 760 }}>
          <NeonEyebrow>ABOUT</NeonEyebrow>
          <Typography variant="body1" sx={{ fontSize: 17, lineHeight: 1.7, mt: 1, whiteSpace: "pre-wrap" }}>
            {company.description}
          </Typography>
        </Box>
      )}

      <NeonDivider sx={{ my: 4 }} />

      <NeonEyebrow>THEIR CONTENT</NeonEyebrow>
      <Typography variant="h3" sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, mt: 1, mb: 3 }}>
        Videos &amp; articles from {company.name}
      </Typography>
      {content.length === 0 ? (
        <Alert severity="info">Nothing published from this company yet — check back soon.</Alert>
      ) : (
        <Grid container spacing={2}>
          {content.map((c) => (
            <Grid key={c.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <ContentCard item={c} />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
}
